import { useState, useEffect, useCallback, memo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { CASE_STUDIES, VIDEO_TYPES } from "@/lib/site";
type CaseStudy = { id: string; slug: string; name: string; display_name?: string; type: string };
const STATIC_STUDIES: CaseStudy[] = CASE_STUDIES.map((c) => ({ id: c.slug, slug: c.slug, name: c.client, type: c.type }));
import stagLogo from "@/assets/stag-logo.png";
type NavLink = { name: string; href: string; children?: { name: string; href: string }[] };
const NAV_LINKS: NavLink[] = [
  { name: "Lead gen", href: "/video-growth-engine" },
  { name: "Video production", href: "/commercial-video", children: VIDEO_TYPES.map((v) => ({ name: v.name, href: `/${v.slug}` })) },
  { name: "Our results", href: "/our-results" },
  { name: "About", href: "/about" },
  { name: "Contact", href: "/#contact" },
];

// Helper to normalize slug to URL path
const getSlugPath = (slug: string) => {
  return `/our-results#${slug}`;
};

// Group case studies by their service type
const groupCaseStudiesByType = (studies: CaseStudy[]) => {
  const grouped: Record<string, CaseStudy[]> = {};
  studies.forEach((study) => {
    const type = study.type || "Other";
    if (!grouped[type]) {
      grouped[type] = [];
    }
    grouped[type].push(study);
  });
  return grouped;
};

const Navigation = memo(() => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileWorkOpen, setMobileWorkOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogoClick = useCallback(
    (e: React.MouseEvent) => {
      if (isHomePage) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
      }
    },
    [isHomePage, navigate],
  );

  const handleNavClick = useCallback(
    (e: React.MouseEvent, href: string) => {
      if (isHomePage) {
        return;
      } else {
        e.preventDefault();
        navigate("/" + href);
      }
    },
    [isHomePage, navigate],
  );

  const navItems = [
    { name: "Work", href: "#work" },
    { name: "About", href: "#about" },
    { name: "News", href: "/news-archive", isPage: true },
    { name: "Contact", href: "#contact" },
  ];

  const servicePages = [
    { name: "Video Growth Engine", href: "/video-growth-engine" },
    { name: "Video production", href: "/commercial-video" },
    { name: "Videographer Wrexham", href: "/videographer-wrexham" },
    { name: "Videographer North Wales", href: "/videographer-north-wales" },
    { name: "Videographer Shrewsbury", href: "/videographer-shrewsbury" },
    { name: "Videographer Shropshire", href: "/videographer-shropshire" },
    { name: "Other services", href: "/other-services" },
  ];

  // Defer case studies fetch to avoid blocking LCP - use staleTime to prevent refetches
  const caseStudies = STATIC_STUDIES;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 py-4">
      <div className="container mx-auto px-6">
        <div
          className={`transition-all duration-300 rounded-full ${
            isScrolled
              ? "bg-primary shadow-[0_8px_32px_-12px_rgba(0,0,0,0.25)]"
              : "bg-transparent"
          }`}
        >
          <div className="flex items-center justify-between pl-6 pr-3 lg:pr-3 py-3 max-lg:pr-6">
            <div className="flex items-center">
              <a href={isHomePage ? "#" : "/"} onClick={handleLogoClick} className={`cursor-pointer inline-flex transition-all duration-200`}>
                <img
                  src={stagLogo}
                  alt="Stag Marketing"
                  width={40}
                  height={40}
                  // @ts-expect-error fetchpriority is valid HTML but React prefers camelCase
                  fetchpriority="high"
                  className={`h-10 w-auto brightness-0 invert transition-all duration-200`}
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8 ml-auto pl-12">
              {NAV_LINKS.map((l) => l.children ? (
                <div key={l.href} className="group relative">
                  <Link to={l.href} className={`flex items-center gap-1 font-medium transition-colors duration-200 ${"text-white hover:text-white"}`}>
                    {l.name} <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                  </Link>
                  <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <div className="w-64 rounded-2xl border border-border bg-background p-2 shadow-xl">
                      {l.children.map((c) => (
                        <Link key={c.href} to={c.href} className="block rounded-xl px-4 py-2.5 text-sm font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-primary">{c.name}</Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link key={l.href} to={l.href} className={`font-medium transition-colors duration-200 ${"text-white hover:text-white"}`}>
                  {l.name}
                </Link>
              ))}
              <Button
                size="lg"
                className={`rounded-full transition-colors duration-300 ${
                  isScrolled
                    ? "bg-white !text-primary hover:bg-white/90 border-2 border-white"
                    : "bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary"
                }`}
                onClick={() => window.location.href = "tel:+447403682194"}
              >
                Call us
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden transition-colors ${"text-white"}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-3 overflow-hidden rounded-3xl bg-primary text-primary-foreground shadow-2xl animate-fade-in max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="divide-y divide-primary-foreground/20 px-6">
              {NAV_LINKS.map((l) => l.children ? (
                <div key={l.href}>
                  <button type="button" onClick={() => setMobileServicesOpen((o) => !o)} aria-expanded={mobileServicesOpen} className="flex w-full items-center justify-between py-4 text-left text-4xl font-bold text-primary-foreground">
                    {l.name} <ChevronDown className={`h-6 w-6 shrink-0 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="mb-4 grid gap-1 rounded-2xl bg-primary-foreground/10 p-3">
                      <Link to={l.href} onClick={() => setIsMobileMenuOpen(false)} className="rounded-xl px-3 py-2 font-semibold text-primary-foreground">All video production</Link>
                      {l.children.map((c) => (
                        <Link key={c.href} to={c.href} onClick={() => setIsMobileMenuOpen(false)} className="rounded-xl px-3 py-2 text-primary-foreground active:bg-primary-foreground/10">{c.name}</Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={l.href} to={l.href} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-between py-4 text-4xl font-bold text-primary-foreground">
                  {l.name} <ChevronRight className="h-6 w-6 shrink-0" />
                </Link>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 p-6 pt-4">
              <a href="tel:+447403682194" className="rounded-full bg-primary-foreground py-3 text-center font-semibold text-primary">Call us</a>
              <a href="https://wa.me/447403682194" target="_blank" rel="noopener noreferrer" className="rounded-full border-2 border-primary-foreground py-3 text-center font-semibold text-primary-foreground">WhatsApp</a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
});

Navigation.displayName = "Navigation";
export default Navigation;
